import type { NeoNode, NeoRel } from '@neo4j-ndl/react';
import type { FleetManagementTypes } from '@nx/state';
import { consoleApi, useActiveProject } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { concat, find } from 'lodash-es';
import { useEffect, useState } from 'react';

import { addDatabaseNode, addServerNode, getDbmsNode, getServerToDatabaseRelationship } from './node-model';

export type TypedEntity = {
  deployment?: FleetManagementTypes.DeploymentDetails;
  server?: FleetManagementTypes.Server;
  database?: FleetManagementTypes.Database;
};

export const useTopologyGraph = ({ deployments }: { deployments: FleetManagementTypes.DeploymentDetails[] }) => {
  const [nodes, setNodes] = useState<NeoNode[]>([]);
  const [rels, setRels] = useState<NeoRel[]>([]);
  const [knownDeployments, setKnownDeployments] = useState<string[] | undefined>(undefined);
  const [selectedEntity, setSelectedEntity] = useState<TypedEntity | undefined>(undefined);

  const activeProject = useActiveProject();
  const getProjectMembersRes = consoleApi.useListProjectMembersQuery(activeProject.id);

  const AddOrRemove = (adding: boolean, newNodes: NeoNode[], newRels: NeoRel[]) => {
    if (adding) {
      setNodes(concat(...newNodes.filter((n) => !nodes.some((existing) => existing.id === n.id)), ...nodes));
      setRels(concat(...newRels.filter((n) => !rels.some((existing) => existing.id === n.id)), ...rels));
    } else {
      setNodes(nodes.filter((n) => isNullish(find(newNodes, (n2) => n2.id === n.id))));
      setRels(rels.filter((r) => isNullish(find(newRels, (r2) => r2.id === r.id))));
    }
  };

  const expandOrDismissAll = (deploymentIds: string[], adding: boolean) => {
    const newNodes: NeoNode[] = [];
    const newRels: NeoRel[] = [];

    deploymentIds.forEach((id) => {
      const deployment = deployments.find((d) => d.deploymentId === id);
      if (isNullish(deployment)) {
        return;
      }

      deployment.dbms?.servers.forEach((s) => {
        addServerNode(s, newNodes, newRels, deployment);
        s.databaseInstances.forEach((di) => {
          newRels.push(getServerToDatabaseRelationship(di, deployment.deploymentId));
        });
      });

      deployment.dbms?.databases.forEach((d) => {
        addDatabaseNode(newNodes, d, newRels, deployment);
      });
    });

    AddOrRemove(adding, newNodes, newRels);
  };

  const toggleDeployment = (deployment: FleetManagementTypes.DeploymentDetails) => {
    const newNodes: NeoNode[] = [];
    const newRels: NeoRel[] = [];
    // Figure out if the toggle should add or remove things
    let adding = true;

    deployment.dbms?.servers.forEach((s) => {
      if (nodes.find((n) => n.id === s.serverId)) {
        adding = false;
      }
      addServerNode(s, newNodes, newRels, deployment);
    });

    deployment.dbms?.databases.forEach((d) => {
      if (nodes.find((n) => n.id === `db${d.databaseId}`)) {
        adding = false;
      }
      addDatabaseNode(newNodes, d, newRels, deployment);
    });

    AddOrRemove(adding, newNodes, newRels);
  };

  const toggleServers = (s: FleetManagementTypes.Server, deploymentId: string) => {
    const newRels: NeoRel[] = [];
    // Figure out if the toggle should add or remove things
    let adding = true;

    s.databaseInstances.forEach((di) => {
      const rel = getServerToDatabaseRelationship(di, deploymentId);
      if (rels.find((r) => r.id === rel.id)) {
        adding = false;
      }
      newRels.push(rel);
    });

    AddOrRemove(adding, [], newRels);
  };

  const toggle = (id: string) => {
    const deployment = deployments.find((d) => d.deploymentId === id);
    if (isNotNullish(deployment)) {
      toggleDeployment(deployment);
    }
    for (const d of deployments) {
      const server = d.dbms?.servers.find((s) => s.serverId === id);
      if (isNotNullish(server)) {
        toggleServers(server, d.deploymentId);
        return;
      }
    }
  };

  const lookupEntity = (id: string | undefined): TypedEntity | undefined => {
    if (isNullish(id)) {
      return undefined;
    }
    const deployment = deployments.find((d) => d.deploymentId === id);
    if (isNotNullish(deployment)) {
      return { deployment: deployment };
    }
    for (const d of deployments) {
      const server = d.dbms?.servers.find((s) => s.serverId === id);
      if (isNotNullish(server)) {
        return { server: server };
      }
      const database = d.dbms?.databases.find((db) => `db${db.databaseId}${db.name}${d.deploymentId}` === id);
      if (isNotNullish(database)) {
        return { database: database };
      }
    }
    // Oopsie, not found!
    return undefined;
  };

  const selectNode = (id: string | undefined) => {
    setNodes(
      nodes.map((n) => {
        return { ...n, selected: n.id === id };
      }),
    );
    setSelectedEntity(lookupEntity(id));
  };

  useEffect(() => {
    let every = false;
    if (isNotNullish(knownDeployments)) {
      every =
        deployments.every((d, i) => d.deploymentId === knownDeployments[i]) &&
        knownDeployments.every((id, i) => deployments[i]?.deploymentId === id);
    }

    // Populate initial DBMS nodes
    if (!every) {
      const newRels: NeoRel[] = [];
      const newNodes = deployments.map((d) => getDbmsNode(d, getProjectMembersRes.data));

      // If only one DBMS, then expand by default
      if (deployments.length === 1 && isNotNullish(deployments[0])) {
        const [deployment] = deployments;
        deployment.dbms?.servers.forEach((s) => {
          addServerNode(s, newNodes, newRels, deployment);
        });
        deployment.dbms?.databases.forEach((d) => {
          addDatabaseNode(newNodes, d, newRels, deployment);
        });
      }

      setNodes(newNodes);
      setRels(newRels);
      setKnownDeployments(deployments.map((d) => d.deploymentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deployments]);

  return { nodes, rels, toggle, expandOrDismissAll, selectNode, selectedEntity };
};
